import React, { useCallback } from 'react';
import i18next from 'i18next';

import { CheckboxInput } from '../CheckboxInput';
import { TextInput } from '../TextInput';
import { SelectInput } from '../SelectInput';

import TemperatureStep from './checklist_temperature_step';
import SelectionStep from './checklist_selection_step';

import config from "config";
import ContentStep from './checklist_content_step';
import NoteStep from './checklist_note_step';

const ChecklistTaskStep = ({ step, readOnly, stepTypes, checklistDeviceTags, onStepUpdate, onStepRemove, onCorrectiveAction, inCorrectiveAction }) => {
    const typeOptions = stepTypes.map((stepType) => ({ value: stepType.id, label: i18next.t(`checklist-step-types.${stepType.token}`), type: stepType }));
    const checklistTagOptions = [{ value: -1, label: `<${i18next.t('none')}>`, tag: null }].concat(checklistDeviceTags.map((tag) => ({value: tag.id, label: tag.name, tag: tag})));

    const toggleRequired = useCallback(() => {
        if (!readOnly) {
            onStepUpdate({ ...step, required: !step.required });
        }
    }, [step, onStepUpdate]);

    const toggleClearOnRestart = useCallback(() => {
        if (!readOnly) {
            onStepUpdate({ ...step, clearOnRestart: !step.clearOnRestart });
        }
    }, [step, onStepUpdate]);

    const updateName = useCallback((name) => {
        let updatedStep = { ...step, name: name };

        if (step.type === 'temperature') {
            if (step.constraintCorrectiveAction) {
                updatedStep.constraintCorrectiveAction.name = `${step.name} - Constraint`;
            }
        }

        if (step.type === 'selection' && step.checklistTaskSelectionOptions) {
            updatedStep.checklistTaskSelectionOptions.forEach((selection) => {
                if (selection.correctiveAction) {
                    selection.correctiveAction.name = `${step.name} - ${selection.name}`;
                }
            })
        }

        onStepUpdate(updatedStep);
    }, [step, onStepUpdate]);

    const updateDescription = useCallback((description) => {
        onStepUpdate({ ...step, description: description });
    }, [step, onStepUpdate]);

    const updateStepType = useCallback((type) => {
        let updatedStep = { ...step, type: type.token, checklistTaskOptionType: type }

        if (type.token === 'selection') {
            updatedStep.maxCount = 1;
            updatedStep.checklistTaskSelectionOptions = [];
        } else if (type.token === 'note') {
            updatedStep.useNoteAsDescription = false;
        }

        onStepUpdate(updatedStep);
    }, [step, onStepUpdate]);

    return (
        <div>
            <ul className="list-inline clearfix">
                <li>
                    <CheckboxInput
                        input={{ checked: step.required ? step.required : false, onChange: toggleRequired, disabled: readOnly }}
                        label={i18next.t("form-labels.required")}
                        className="inline"
                        meta={{}}
                    />
                </li>
                {
                    step.type !== "content" && inCorrectiveAction === false && 
                    <li>
                        <CheckboxInput
                            input={{ checked: step.clearOnRestart ? step.clearOnRestart : false, onChange: toggleClearOnRestart, disabled: readOnly }}
                            label="Clear on Restart"
                            className="inline"
                            meta={{}}
                        />
                    </li>
                }
                {
                    !readOnly &&
                    <li className="pull-right">
                        <img src={`${config.WEB_BASE_URL}/images/icon/delete-trash-can.svg`} name="delete" alt={i18next.t("delete")} onClick={() => onStepRemove(step)} />
                    </li>
                }
            </ul>

            <div className="form-group">
                <TextInput
                    input={{
                        value: step.name ? step.name : "",
                        onChange: (event) => updateName(event.target.value),
                    }}
                    type="text"
                    label={i18next.t("form-labels.name")}
                    readOnly={readOnly}
                    meta={step.errors && step.errors.name ? { touched: true, error: step.errors.name } : {}}
                />
            </div>

            <div className="form-group">
                <label>{i18next.t("form-labels.type")}</label>
                <SelectInput
                    input={{
                        value: step.checklistTaskOptionType ? { label: step.checklistTaskOptionType.name } : null,
                        onChange: (value) => updateStepType(value.type),
                    }}
                    isDisabled={readOnly}
                    options={typeOptions}
                    isSearchable={true}
                    meta={step.errors && step.errors.type ? { touched: true, error: step.errors.type } : {}}
                />
            </div>

            {step && step.checklistTaskOptionType && step.checklistTaskOptionType.token ? (
                <div>
                    <div className="form-group">
                        <TextInput
                            input={{
                                value: step.description ? step.description : "",
                                onChange: (event) => updateDescription(event.target.value),
                            }}
                            type="text"
                            label={i18next.t("form-labels.description-optional")}
                            readOnly={readOnly}
                            meta={{}}
                        />
                    </div>

                    <TemperatureStep
                        step={step}
                        readOnly={readOnly}
                        checklistTagOptions={checklistTagOptions}
                        onCorrectiveAction={onCorrectiveAction}
                        onStepUpdate={onStepUpdate}
                    />

                    <SelectionStep 
                        step={step}
                        readOnly={readOnly}
                        onStepUpdate={onStepUpdate}
                        onCorrectiveAction={onCorrectiveAction}
                    />

                    <ContentStep
                        step={step}
                        readOnly={readOnly}
                        onStepUpdate={onStepUpdate}
                    />

                    <NoteStep
                        step={step}
                        readOnly={readOnly}
                        onStepUpdate={onStepUpdate}
                        inCorrectiveAction={inCorrectiveAction}
                    />
                </div>
            ) : (
                <div>
                    <br />
                    <p style={{ textAlign: "center", color: "#999" }}>~ {i18next.t("messages.select-type")} ~</p>
                </div>
            )}
        </div>
    );
}

export default ChecklistTaskStep;